/**
 * Serve-line inventory, client side (PROD-1278) — pure helpers between the
 * `GET /api/cafeteria/inventory` rows and the Inventory page: the per-row
 * status badge and the count formatting. The serve screen reads the same
 * rows only for the register's sold-out set (Sep 14 team review removed the
 * per-row "N in stock" pills and the tally folding that fed them; uncommitted
 * school tallies live on the serve draft and the Inventory page shows the
 * server's numbers as they are).
 */

import type { InventoryRowWire } from '@/api/cafeteria-inventory'

export type InventoryRowStatus = 'sold-out' | 'none-left' | 'counted'

/**
 * Short status for the Inventory page's per-row badge (null = nothing to flag).
 * A Delivered number that is still the kitchen's plan, or the worker's own
 * edit, is not flagged: cafeteria mode no longer confirms deliveries
 * (PROD-1277), so where the number came from is not a task here.
 */
export function inventoryRowStatus(
  row: Pick<InventoryRowWire, 'soldOut' | 'left' | 'deliveredBasis'>,
): InventoryRowStatus | null {
  if (row.soldOut) {
    return 'sold-out'
  }
  if (row.left === 0) {
    return 'none-left'
  }
  if (row.deliveredBasis === 'ADJUSTED') {
    return 'counted'
  }
  return null
}

/** The status badge's words on the Inventory page. */
export const INVENTORY_STATUS_LABEL: Record<InventoryRowStatus, string> = {
  'sold-out': 'Sold out',
  'none-left': 'None left',
  counted: 'Counted by hand',
}

/**
 * Badge tone per status: sold out is the one the register acts on, none left
 * is a warning the worker should act on, counted by hand is a note about where
 * the numbers came from.
 */
export function inventoryStatusBadgeVariant(status: InventoryRowStatus): 'destructive' | 'warning' | 'secondary' {
  switch (status) {
    case 'sold-out':
      return 'destructive'
    case 'none-left':
      return 'warning'
    default:
      return 'secondary'
  }
}

/** A count cell's text: an unknown value is a dash, never a fake zero. */
export function formatInventoryCount(value: number | null): string {
  return value === null ? '—' : String(value)
}
